import api from "./api";

export type ApiError =
  | { type: "serverValidation"; errors?: Record<string, string> }
  | { type: "network"; message?: string }
  | { type: "server"; message?: string };

export interface BookingsResponse {
  data: any[];
  meta?: any;
  employees?: any[];
}
export interface BookingEditResponse {
  booking: any;
  business_services: {
    time: number;
    time_type: string;
    name: string;
    price: number;
    [key: string]: any;
  }[];
  employees: any[];
  selected_booking_user: any[];
  tax: any;
}
interface BookingFilters {
  filter_status?: string;
  startDate?: string;
  endDate?: string;
}

export const getBookings = async (
  filters: BookingFilters
): Promise<BookingsResponse> => {
  const { filter_status, startDate, endDate } = filters;
  const errors: Record<string, string> = {};

  if (!filter_status && filter_status !== "all") {
    errors.filter_status = "Filter status is required";
  }
  if (!startDate) errors.startDate = "Start date is required";
  if (!endDate) errors.endDate = "End date is required";

  if (Object.keys(errors).length > 0) {
    throw { type: "serverValidation", errors } as ApiError;
  }

  try {
    const response = await api.get<BookingsResponse>("/admin/bookings", {
      params: {
        ...(filter_status !== "all" && { filter_status }),
        startDate: startDate,
        endDate: endDate,
      },

      headers: {
        Accept: "application/json",
      },
    });

    console.log(
      "Bookings fetched successfully with filters:",
      filters,
      "Response:",
      response.data

    );

    return response.data;
  } catch (err: any) {
    if (err?.response?.status === 422 && err.response.data?.errors) {
      const fieldErrors: Record<string, string> = {};

      Object.entries(err.response.data.errors).forEach(([field, msgs]: any) => {
        fieldErrors[field] = Array.isArray(msgs)
          ? msgs.join(" ")
          : String(msgs);
      });

      throw {
        type: "serverValidation",
        errors: fieldErrors,
      } as ApiError;
    }
    if (!err.response) {
      console.error("Network error while fetching bookings:", err);
      throw {
        type: "network",
        message: "Network error. Please check your internet connection.",
      } as ApiError;
    }
    console.error(
      "Failed to fetch bookings:",
      err.response?.data || err.message
    );
    throw {
      type: "server",
      message: err.response?.data?.message || err.message,
    } as ApiError;
  }
};

export const getBookingsWithoutParams = async (): Promise<BookingsResponse> => {
  try {
    const response = await api.get<BookingsResponse>("/admin/bookings", {
      headers: {
        Accept: "application/json",
      },
    });

    console.log(
      "Bookings fetched successfully without parameters",
      "Response:",
      response.data
    );

    return response.data;
  } catch (err: any) {
    if (!err.response) {
      console.error("Network error while fetching bookings without params:", err);
      throw {
        type: "network",
        message: "Network error. Please check your internet connection.",
      } as ApiError;
    }
    console.error(
      "Failed to fetch bookings without params:",
      err.response?.data || err.message
    );
    throw {
      type: "server",
      message: err.response?.data?.message || err.message,
    } as ApiError;
  }
};

export const getBookingEdit = async (
  bookingId: number
): Promise<BookingEditResponse> => {
  if (!bookingId) {
    throw {
      type: "serverValidation",
      errors: { bookingId: "Booking ID is required" },
    } as ApiError;
  }

  try {
    const response = await api.get<BookingEditResponse>(
      `/admin/bookings/${bookingId}/edit`,
      {
        headers: {
          Accept: "application/json",
        },
      }
    );

    return response.data;
  } catch (err: any) {
    if (err?.response?.status === 422 && err.response.data?.errors) {
      const fieldErrors: Record<string, string> = {};

      Object.entries(err.response.data.errors).forEach(([field, msgs]: any) => {
        fieldErrors[field] = Array.isArray(msgs)
          ? msgs.join(" ")
          : String(msgs);
      });

      throw {
        type: "serverValidation",
        errors: fieldErrors,
      } as ApiError;
    }
    if (!err.response) {
      console.error("Network error while fetching booking edit:", err);
      throw {
        type: "network",
        message: "Network error. Please check your internet connection.",
      } as ApiError;
    }

    console.error(
      "Failed to fetch booking edit:",
      err.response?.data || err.message
    );
    throw {
      type: "server",
      message: err.response?.data?.message || err.message,
    } as ApiError;
  }
};

export const getBookingDetails = async (
  bookingId: number
): Promise<any> => {
  if (!bookingId) {
    throw {
      type: "serverValidation",
      errors: { bookingId: "Booking ID is required" },
    } as ApiError;
  }

  try {
    const response = await api.get<any>(
      `/admin/bookings/${bookingId}`,
      {
        headers: {
          Accept: "application/json",
        },
      }
    );

    console.log("Booking details fetched:", response.data);

    return response.data;
  } catch (err: any) {
    if (!err.response) {
      console.error("Network error while fetching booking details:", err);
      throw {
        type: "network",
        message: "Network error. Please check your internet connection.",
      } as ApiError;
    }

    console.error(
      "Failed to fetch booking details:",
      err.response?.data || err.message
    );
    throw {
      type: "server",
      message: err.response?.data?.message || err.message,
    } as ApiError;
  }
};


export interface FilterServicesRequest {
  stylist_id: number | null;
  date: string;
  start_time: string;
  end_time: string;
}

export const filterServices = async (
  payload: FilterServicesRequest
) => {
  const errors: Record<string, string> = {};

  if (!payload.stylist_id)
    errors.stylist_id = "Stylist ID is required";
  if (!payload.date)
    errors.date = "Date is required";
  if (!payload.start_time)
    errors.start_time = "Start time is required";
  if (!payload.end_time)
    errors.end_time = "End time is required";

  if (Object.keys(errors).length > 0) {
    throw { type: "serverValidation", errors } as ApiError;
  }

  try {
    const response = await api.post(
      "/admin/bookings/filter-services",
      payload,
      {
        headers: {
          Accept: "application/json",
        },
      }
    );

    console.log("Filter services success:", response.data);

    return response.data;
  } catch (err: any) {
    if (err?.response?.status === 422 && err.response.data?.errors) {
      const fieldErrors: Record<string, string> = {};

      Object.entries(err.response.data.errors).forEach(([field, msgs]: any) => {
        fieldErrors[field] = Array.isArray(msgs)
          ? msgs.join(" ")
          : String(msgs);
      });

      throw {
        type: "serverValidation",
        errors: fieldErrors,
      } as ApiError;
    }

    if (!err.response) {
      console.error("Network error in filter-services:", err);
      throw {
        type: "network",
        message: "Network error. Please check your internet connection.",
      } as ApiError;
    }

    console.error(
      "Failed to filter services:",
      err.response?.data || err.message
    );

    throw {
      type: "server",
      message: err.response?.data?.message || err.message,
    } as ApiError;
  }
};


export const updateSingleBookingStatus = async (payload: any) => {
  const errors: Record<string, string> = {};

  if (!payload.booking_id) errors.booking_id = "Booking ID is required";
  if (!payload.status) errors.status = "Status is required";

  if (Object.keys(errors).length > 0) {
    throw { type: "serverValidation", errors } as ApiError;
  }

  try {
    const response = await api.post(
      "/admin/bookings/update-single-status",
      payload,
      {
        headers: {
          Accept: "application/json",
        },
      }
    );

    console.log("Booking status updated:", response.data);

    return response.data;
  } catch (err: any) {
    if (err?.response?.status === 422 && err.response.data?.errors) {
      const fieldErrors: Record<string, string> = {};
      Object.entries(err.response.data.errors).forEach(([field, msgs]: any) => {
        fieldErrors[field] = Array.isArray(msgs) ? msgs.join(" ") : String(msgs);
      });

      throw { type: "serverValidation", errors: fieldErrors } as ApiError;
    }

    if (!err.response) {
      console.error("Network error in update-single-status:", err);
      throw { type: "network", message: "Network error. Please check your internet connection." } as ApiError;
    }

    console.error(
      "Failed to update booking status:",
      err.response?.data || err.message
    );
    throw { type: "server", message: err.response?.data?.message || err.message } as ApiError;
  }
};

export const requestCancelBooking = async (bookingId: number) => {
  if (!bookingId) {
    throw {
      type: "serverValidation",
      errors: { bookingId: "Booking ID is required" },
    } as ApiError;
  }

  try {
    const response = await api.post(
      `/admin/bookings/${bookingId}/request-cancel`,
      {},
      {
        headers: {
          Accept: "application/json",
        },
      }
    );

    console.log("Cancel request success:", response.data);

    return response.data;
  } catch (err: any) {
    if (err?.response?.status === 422 && err.response.data?.errors) {
      const fieldErrors: Record<string, string> = {};

      Object.entries(err.response.data.errors).forEach(([field, msgs]: any) => {
        fieldErrors[field] = Array.isArray(msgs)
          ? msgs.join(" ")
          : String(msgs);
      });

      throw {
        type: "serverValidation",
        errors: fieldErrors,
      } as ApiError;
    }

    if (!err.response) {
      console.error("Network error in request-cancel:", err);
      throw {
        type: "network",
        message: "Network error. Please check your internet connection.",
      } as ApiError;
    }

    console.error(
      "Failed to request cancellation:",
      err.response?.data || err.message
    );
    throw {
      type: "server",
      message: err.response?.data?.message || err.message,
    } as ApiError;
  }
};

export interface UpdateBookingDatePayload {
  id: number;
  date_time: string;
}

export const updateBookingDate = async (
  payload: UpdateBookingDatePayload
) => {
  const errors: Record<string, string> = {};

  if (!payload.id) errors.id = "Booking ID is required";
  if (!payload.date_time) errors.date_time = "Date and time are required";

  if (Object.keys(errors).length > 0) {
    throw { type: "serverValidation", errors } as ApiError;
  }

  try {
    const response = await api.put(
      `/admin/bookings/${payload.id}/update-booking-date`,
      payload,
      {
        headers: {
          Accept: "application/json",
        },
      }
    );

    console.log("Booking date updated successfully:", response.data);

    return response.data;
  } catch (err: any) {
    if (err?.response?.status === 422 && err.response.data?.errors) {
      const fieldErrors: Record<string, string> = {};
      Object.entries(err.response.data.errors).forEach(([field, msgs]: any) => {
        fieldErrors[field] = Array.isArray(msgs) ? msgs.join(" ") : String(msgs);
      });

      throw { type: "serverValidation", errors: fieldErrors } as ApiError;
    }

    if (!err.response) {
      console.error("Network error in updateBookingDate:", err);
      throw { type: "network", message: "Network error. Please check your internet connection." } as ApiError;
    }

    console.error(
      "Failed to update booking date:",
      err.response?.data || err.message
    );
    throw { type: "server", message: err.response?.data?.message || err.message } as ApiError;
  }
};

export const updateBooking = async (id: number | string, payload: any) => {
  try {
    const response = await api.put(`/admin/bookings/${id}`, payload);
    return response.data;
  } catch (err: any) {
    console.error("Failed to update booking:", err.response?.data || err.message);
    throw err;
  }
};


export interface AddQuickPaymentPayload {
  booking_id: number;
  employee_id: number;
  transaction_id: string;
  amount: number;
  status?: "completed" | "pending";
  gateway?: string;
  change_due?: number;
  currency_id?: number;
  amount_paid?: number;
  amount_remaining?: number;
  paid_on?: string;
  transfer_status?: string;
  is_quick_payment?: 1 | 0;
  company_id: number;
  consultation_form_ids?: number[];
}

export const addQuickPayment = async (payload: AddQuickPaymentPayload) => {
  const errors: Record<string, string> = {};

  if (!payload.booking_id) errors.booking_id = "Booking ID is required";
  if (!payload.employee_id) errors.employee_id = "Employee ID is required";
  if (!payload.transaction_id) errors.transaction_id = "Transaction ID is required";
  if (payload.amount === undefined || payload.amount === null)
    errors.amount = "Amount is required";
  if (!payload.company_id) errors.company_id = "Company ID is required";

  if (Object.keys(errors).length > 0) {
    throw { type: "serverValidation", errors } as ApiError;
  }

  try {
    const response = await api.post("/admin/bookings/add-quick-payment", payload, {
      headers: {
        Accept: "application/json",
      },
    });

    console.log("Quick payment added successfully:", response.data);
    return response.data;
  } catch (err: any) {
    if (err?.response?.status === 422 && err.response.data?.errors) {
      const fieldErrors: Record<string, string> = {};
      Object.entries(err.response.data.errors).forEach(([field, msgs]: any) => {
        fieldErrors[field] = Array.isArray(msgs) ? msgs.join(" ") : String(msgs);
      });

      throw { type: "serverValidation", errors: fieldErrors } as ApiError;
    }

    if (!err.response) {
      console.error("Network error in addQuickPayment:", err);
      throw { type: "network", message: "Network error. Please check your internet connection." } as ApiError;
    }

    console.error(
      "Failed to add quick payment:",
      err.response?.data || err.message
    );
    throw { type: "server", message: err.response?.data?.message || err.message } as ApiError;
  }
};

export interface AddPaymentFromShowPayload {
  amountPaid: number;
  total: number;
  paymentMode: string | Array<{ type: string; amount: number }>;
  changeDue?: number;
}

export const addPaymentFromShow = async (
  bookingId: number | string,
  payload: AddPaymentFromShowPayload,
) => {
  try {
    const response = await api.post(
      `/admin/bookings/${bookingId}/add-payment-from-show`,
      {
        amountPaid: payload.amountPaid,
        total: payload.total,
        paymentMode: payload.paymentMode,
        changeDue: payload.changeDue ?? 0,
      },
      {
        headers: {
          Accept: "application/json",
        },
      },
    );

    return response.data;
  } catch (err: any) {
    if (err?.response?.status === 422 && err.response.data?.errors) {
      const fieldErrors: Record<string, string> = {};
      Object.entries(err.response.data.errors).forEach(([field, msgs]: any) => {
        fieldErrors[field] = Array.isArray(msgs) ? msgs.join(" ") : String(msgs);
      });

      throw { type: "serverValidation", errors: fieldErrors } as ApiError;
    }

    if (!err.response) {
      throw {
        type: "network",
        message: "Network error. Please check your internet connection.",
      } as ApiError;
    }

    throw {
      type: "server",
      message: err.response?.data?.message || err.message,
    } as ApiError;
  }
};


export interface CustomersResponse {
  success: boolean;
  data: any;
  message: string;
}

// Request params type
interface CustomerFilters {
  per_page?: number;
  search?: string;
}

export const getCustomers = async (
  filters: CustomerFilters
): Promise<CustomersResponse> => {
  const errors: Record<string, string> = {};

  if (!filters.search) {
    errors.search = "Search term is required";
  }
  if (!filters.per_page) {
    errors.per_page = "per_page is required";
  }

  if (Object.keys(errors).length > 0) {
    throw { type: "serverValidation", errors };
  }

  try {
    const response = await api.get<CustomersResponse>(
      "/admin/customers",
      {
        params: {
          per_page: filters.per_page,
          search: filters.search,
        },
        headers: {
          Accept: "application/json",
        },
      }
    );

    console.log("Customers fetched:", response.data);

    return response.data;
  } catch (err: any) {
    if (err?.response?.status === 422 && err.response.data?.errors) {
      const fieldErrors: Record<string, string> = {};

      Object.entries(err.response.data.errors).forEach(([field, msgs]: any) => {
        fieldErrors[field] = Array.isArray(msgs)
          ? msgs.join(" ")
          : String(msgs);
      });

      throw {
        type: "serverValidation",
        errors: fieldErrors,
      };
    }

    if (!err.response) {
      console.error("Network error while fetching customers:", err);
      throw {
        type: "network",
        message: "Network error. Please check your connection.",
      };
    }

    console.error("Failed to fetch customers:", err.response?.data || err.message);

    throw {
      type: "server",
      message: err.response?.data?.message || err.message,
    };
  }
};


// -------------------------
// Types
// -------------------------

export interface CreateCustomerPayload {
  name: string;
  mobile: string;
  email?: string;
  skin_type?: string;
  landline?: string;
  address?: string;
}

export interface CreateCustomerResponse {
  success: boolean;
  message: string;
  data?: any; // backend-created customer object
}

export type CreateCustomerError =
  | { type: "serverValidation"; errors: Record<string, string> }
  | { type: "network"; message: string }
  | { type: "server"; message: string };


// -------------------------
// API Function
// -------------------------

export const createCustomer = async (
  payload: CreateCustomerPayload
): Promise<CreateCustomerResponse> => {
  // ---------- Client-side validation ----------
  const errors: Record<string, string> = {};

  if (!payload.name?.trim()) errors.name = "The name field is required.";
  if (!payload.mobile?.trim()) errors.mobile = "The mobile field is required.";

  if (Object.keys(errors).length > 0) {
    throw { type: "serverValidation", errors } as CreateCustomerError;
  }

  // ---------- API Request ----------
  try {
    const response = await api.post<CreateCustomerResponse>(
      "/admin/customers",
      payload,
      {
        headers: {
          Accept: "application/json",
        },
      }
    );

    return response.data;
  } catch (err: any) {
    // ---------- Laravel Validation Error (422) ----------
    if (err?.response?.status === 422 && err.response.data?.errors) {
      const fieldErrors: Record<string, string> = {};

      Object.entries(err.response.data.errors).forEach(([field, msgs]: any) => {
        fieldErrors[field] = Array.isArray(msgs) ? msgs.join(" ") : String(msgs);
      });

      throw { type: "serverValidation", errors: fieldErrors } as CreateCustomerError;
    }

    // ---------- Network Error ----------
    if (!err.response) {
      throw {
        type: "network",
        message: "Network error. Please check your internet connection.",
      } as CreateCustomerError;
    }

    // ---------- Other Server Errors ----------
    throw {
      type: "server",
      message: err.response?.data?.message || "Something went wrong on the server.",
    } as CreateCustomerError;
  }
};



export interface CustomerStats {
  completed: number;
  approved: number;
  pending: number;
  canceled: number;
  in_progress: number;
  total_earnings: number;
}

export interface CustomerDetails {
  id: number;
  company_id: number | null;
  group_id: number | null;
  name: string;
  email: string | null;
  address: string | null;
  date_of_birth: string | null;
  calling_code: string | null;
  mobile: string | null;
  cus_source: string | null;
  landline: string | null;
  mobile_verified: number;
  image: string | null;
  rtl: string | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  country_id: number | null;
  slug: string;
  skin_type: string | null;
  user_image_url: string;
  mobile_with_code: string;
  formatted_mobile: string;
  customer_bookings: any[];
  notes: any[];
  allergy: any[];
  patch_tests: any[];
  images: any[];
}

export interface CustomerDetailsResponse {
  success: boolean;
  message: string;
  data: {
    customer: CustomerDetails;
    productAmount: number;
    serviceSpent: number;
    totalBookingsRefund: number;
    totalSpent: number;
    stats: CustomerStats;
  };
}

export const getCustomerDetails = async (
  id: number
) => {
  if (!id) {
    throw {
      type: "serverValidation",
      errors: {
        id: "Customer ID is required",
      },
    };
  }

  try {
    const response = await api.get(
      `/admin/customers/${id}`,
      {
        headers: {
          Accept: "application/json",
        },
      }
    );

    console.log("Customer details fetched:", response.data);

    return response.data;
  } catch (err: any) {
    // Laravel validation 422
    if (err?.response?.status === 422 && err.response.data?.errors) {
      const fieldErrors: Record<string, string> = {};

      Object.entries(err.response.data.errors).forEach(([field, msgs]: any) => {
        fieldErrors[field] = Array.isArray(msgs)
          ? msgs.join(" ")
          : String(msgs);
      });

      throw {
        type: "serverValidation",
        errors: fieldErrors,
      };
    }

    // Network error
    if (!err.response) {
      console.error("Network error while fetching customer details:", err);
      throw {
        type: "network",
        message: "Network error. Please check your connection.",
      };
    }

    // All other errors
    console.error(
      "Failed to fetch customer details:",
      err.response?.data || err.message
    );

    throw {
      type: "server",
      message: err.response?.data?.message || err.message,
    };
  }
};


export interface DeleteCustomerResponse {
  success: boolean;
  message: string;
}

export const deleteCustomer = async (
  id: number
): Promise<DeleteCustomerResponse> => {
  if (!id) {
    throw {
      type: "serverValidation",
      errors: {
        id: "Customer ID is required",
      },
    };
  }

  try {
    const response = await api.delete<DeleteCustomerResponse>(
      `/admin/customers/${id}`,
      {
        headers: {
          Accept: "application/json",
        },
      }
    );

    console.log("Customer deleted:", response.data);

    return response.data;
  } catch (err: any) {
    // Laravel validation (422)
    if (err?.response?.status === 422 && err.response.data?.errors) {
      const fieldErrors: Record<string, string> = {};

      Object.entries(err.response.data.errors).forEach(([field, msgs]: any) => {
        fieldErrors[field] = Array.isArray(msgs)
          ? msgs.join(" ")
          : String(msgs);
      });

      throw {
        type: "serverValidation",
        errors: fieldErrors,
      };
    }

    // Network error
    if (!err.response) {
      console.error("Network error while deleting customer:", err);
      throw {
        type: "network",
        message: "Network error. Please check your connection.",
      };
    }

    // Other server errors
    console.error(
      "Failed to delete customer:",
      err.response?.data || err.message
    );

    throw {
      type: "server",
      message: err.response?.data?.message || err.message,
    };
  }
};



export interface CustomerQuickDetails {
  id: number;
  company_id: number | null;
  group_id: number | null;
  name: string;
  email: string | null;
  address: string | null;
  date_of_birth: string | null;
  calling_code: string | null;
  mobile: string | null;
  cus_source: string | null;
  landline: string | null;
  mobile_verified: number;
  image: string | null;
  rtl: string | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  country_id: number | null;
  slug: string | null;
  skin_type: string | null;
  user_image_url: string | null;
  mobile_with_code: string | null;
  formatted_mobile: string | null;
}

export interface QuickDetailsResponse {
  success: boolean;
  data: CustomerQuickDetails;
  message: string;
}

export const getCustomerQuickDetails = async (
  id: number
): Promise<QuickDetailsResponse> => {
  if (!id) {
    throw {
      type: "serverValidation",
      errors: {
        id: "Customer ID is required.",
      },
    };
  }

  try {
    const response = await api.get<QuickDetailsResponse>(
      `/admin/customers/${id}/quick-details`,
      {
        headers: {
          Accept: "application/json",
        },
      }
    );

    console.log("Quick details fetched:", response.data);

    return response.data;
  } catch (err: any) {
    // Validation errors (Laravel 422)
    if (err?.response?.status === 422 && err.response.data?.errors) {
      const fieldErrors: Record<string, string> = {};

      Object.entries(err.response.data.errors).forEach(([field, msgs]: any) => {
        fieldErrors[field] = Array.isArray(msgs)
          ? msgs.join(" ")
          : String(msgs);
      });

      throw {
        type: "serverValidation",
        errors: fieldErrors,
      };
    }

    // Network error
    if (!err.response) {
      console.error("Network error while fetching quick details:", err);
      throw {
        type: "network",
        message: "Network error. Please check your connection.",
      };
    }

    // Other server errors
    console.error(
      "Failed to fetch quick customer details:",
      err.response?.data || err.message
    );

    throw {
      type: "server",
      message: err.response?.data?.message || err.message,
    };
  }
};

export interface Customer {
  id: number;
  company_id: number;
  name: string;
  email: string;
  mobile?: string;
  user_image_url?: string;
  formatted_mobile?: string;
  customer_bookings?: any[];
  notes?: any[];
  allergy?: any[];
  patch_tests?: any[];
  images?: any[];
}


export const getAllCustomers = async (
  params?: { search?: string; per_page?: number; page?: number }
) => {
  try {
    const response = await api.get(
      "/admin/customers",
      {
        params: params || {},
        headers: {
          Accept: "application/json",
        },
      }
    );

    console.log("All customers fetched:", response.data);
    return response.data;
  } catch (err: any) {
    // Validation errors (Laravel 422)
    if (err?.response?.status === 422 && err.response.data?.errors) {
      const fieldErrors: Record<string, string> = {};
      Object.entries(err.response.data.errors).forEach(([field, msgs]: any) => {
        fieldErrors[field] = Array.isArray(msgs) ? msgs.join(" ") : String(msgs);
      });
      throw { type: "serverValidation", errors: fieldErrors };
    }

    // Network error
    if (!err.response) {
      console.error("Network error while fetching customers:", err);
      throw { type: "network", message: "Network error. Please check your connection." };
    }

    // Other server errors
    console.error("Failed to fetch customers:", err.response?.data || err.message);
    throw { type: "server", message: err.response?.data?.message || err.message };
  }
};

export const sendBookingReminder = async (
  bookingId: number | string
): Promise<{ message: string }> => {
  try {
    const response = await api.post(
      `/admin/bookings/${bookingId}/send-reminder`,
      {},
      { headers: { Accept: "application/json" } }
    );
    return response.data;
  } catch (err: any) {
    if (!err.response) {
      throw {
        type: "network",
        message: "Network error. Please check your internet connection.",
      } as ApiError;
    }
    throw {
      type: "server",
      message: err.response?.data?.message || err.message,
    } as ApiError;
  }
};

export const sendBookingConfirmation = async (
  bookingId: number | string
): Promise<{ message: string }> => {
  try {
    const response = await api.post(
      `/admin/bookings/${bookingId}/send-confirmation`,
      {},
      { headers: { Accept: "application/json" } }
    );
    return response.data;
  } catch (err: any) {
    if (!err.response) {
      throw {
        type: "network",
        message: "Network error. Please check your internet connection.",
      } as ApiError;
    }
    throw {
      type: "server",
      message: err.response?.data?.message || err.message,
    } as ApiError;
  }
};

export interface DepositPaymentLinkResponse {
  message: string;
  payment_url: string;
  short_url?: string;
  amount: number;
  currency: string;
  booking_id: number;
  session_id?: string;
}

export const createDepositPaymentLink = async (
  bookingId: number | string,
  notify = true
): Promise<DepositPaymentLinkResponse> => {
  try {
    const response = await api.post(
      `/admin/bookings/${bookingId}/deposit-payment-link`,
      { notify },
      { headers: { Accept: "application/json" } }
    );
    return response.data as DepositPaymentLinkResponse;
  } catch (err: any) {
    if (!err.response) {
      throw {
        type: "network",
        message: "Network error. Please check your internet connection.",
      } as ApiError;
    }
    throw {
      type: "server",
      message: err.response?.data?.message || err.message,
    } as ApiError;
  }
};
